/* eslint-disable @reatom/unit-naming-rule */
import { atom } from '@reatom/framework'
import { useAction, useAtom, useCtx } from '@reatom/npm-react'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { TableVirtuoso, type TableVirtuosoHandle } from 'react-virtuoso'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { mergeRefs } from '@root/shared/react'

import { type DataTableModel } from '../atoms'

import { DataTableEmpty } from './data-table-empty'
import { DataTableHeader } from './data-table-header'
import { DataTableRow } from './data-table-row'
import { createColumnGridTemplate, type Column } from './data-table-types'
import { DataTableBackToTop } from './helper-components/data-table-back-to-top'

import type { DataTableContentProps } from './data-table-content'
import type { TableComponents } from 'react-virtuoso'

/**
 * How many pages the table may fetch on its own to fill a viewport.
 *
 * Two covers a tall pane that one page of rows does not reach. Beyond that
 * the rows are not arriving in the scroller and asking again cannot help —
 * see the effect that uses this.
 */
const MAX_AUTO_FILLS = 2

type DataTableContentVirtualProps<
  D extends object,
  ColumnId extends string = string,
  Context extends object | undefined = undefined,
> = Omit<
  DataTableContentProps<D, ColumnId, Context>,
  'virtualization' | 'minWidth' | 'scrollContainerRef'
> & {
  /** Table wrapper, used to measure scroll progress against `customScrollParent`. */
  tableAnchorRef?: React.RefObject<HTMLDivElement | null>
}

export const DataTableContentVirtuoso = <
  D extends object,
  ColumnId extends string = string,
  Context extends object | undefined = undefined,
>({
  columns,
  model,
  rowHeight = 30,
  renderBackgroundLink,
  renderRow,
  getRowStyle,
  disableScroll = false,
  withBackToTop = true,
  floatingActions,
  onScroll,
  context,
  onRowClick,
  emptyTitle,
  renderError,
  renderEmptyMessage,
  increaseViewportBy,
  virtuosoRef: virtuosoRefProp,
  customScrollParent,
  stickyHeaderTop,
  tableAnchorRef,
}: DataTableContentVirtualProps<D, ColumnId, Context>) => {
  const virtuosoRefInternal = useRef<TableVirtuosoHandle>(null)
  const virtuosoRef = mergeRefs([virtuosoRefProp, virtuosoRefInternal])
  const virtuosoScrollerRef = useRef<HTMLElement | Window | null>(null)
  /** Pages this table fetched on its own since it was last full. */
  const autoFilledRef = useRef(0)
  const scrollAtom = useMemo(() => atom(0), [])
  const ctx = useCtx()

  const [itemsState] = useAtom(model.overrideState ?? model)
  const [hasMore] = useAtom(model.hasMoreItems)
  const [loadMoreState] = useAtom(model.loadMoreState)
  const loadMore = useAction(model.loadMore)

  const maybeLoadMore = useCallback(() => {
    if (
      AsyncStateN.isLoading(loadMoreState) ||
      AsyncStateN.isError(loadMoreState) ||
      !hasMore
    ) {
      return
    }

    loadMore()
  }, [loadMoreState, hasMore, loadMore])

  const handleScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
    const { clientHeight, scrollHeight, scrollTop } = e.currentTarget
    scrollAtom(ctx, scrollTop)
    onScroll?.(e)

    if (scrollHeight <= clientHeight) {
      return
    }

    const scrollBottom = scrollHeight - scrollTop - clientHeight

    if (scrollBottom <= rowHeight) {
      maybeLoadMore()
    }
  }

  /**
   * Fill the viewport when a page did not, and then stop.
   *
   * The guards below are not defensive programming; each one is a request
   * storm measured on staging. Opening a token page with the tables on the
   * holders tab produced **nine `/services/tx` reads in a row over eleven
   * seconds**, each starting the millisecond the last one finished, because:
   *
   * - the trades table is mounted but not on screen, so virtuoso renders no
   *   rows and `scrollHeight` never grows past `clientHeight`;
   * - `loadItems` arms `hasMore` *before* awaiting, so the test is already
   *   true while page one is in flight;
   * - this effect depends on `itemsState`, so every page that lands runs it
   *   again, and the test it re-runs can never become false.
   *
   * It was not one spare page. It was an unbounded loop that held a
   * connection for the whole visit and pushed the chart out to 1,687ms and
   * the holder read to 5.5 seconds behind it.
   */
  useEffect(() => {
    const element = virtuosoScrollerRef.current
    if (!element || element instanceof Window) {
      return
    }

    // Laid out at zero height, or on a hidden tab: it renders nothing, so
    // "is it full" is unanswerable and more rows cannot make it true.
    if (element.clientHeight === 0) {
      return
    }

    if (element.scrollHeight > element.clientHeight || !hasMore) {
      autoFilledRef.current = 0
      return
    }

    // Filling a viewport takes a page or two. Past that, the rows are not
    // reaching the scroller and asking again only repeats the question.
    // Scrolling still loads more; this bounds the automatic fill alone.
    if (autoFilledRef.current >= MAX_AUTO_FILLS) {
      return
    }

    autoFilledRef.current += 1
    loadMore()
  }, [hasMore, itemsState, loadMore])

  // With a customScrollParent, react-virtuoso scrolls that external element and
  // does not invoke the component's onScroll prop, so mirror how far the table
  // itself has scrolled past the scrollport top (drives the back-to-top button).
  useEffect(() => {
    const anchor = tableAnchorRef?.current
    if (!customScrollParent || !anchor) {
      return
    }

    const syncScroll = () => {
      const scrolledPast =
        customScrollParent.getBoundingClientRect().top -
        anchor.getBoundingClientRect().top
      scrollAtom(ctx, Math.max(0, scrolledPast))
    }

    syncScroll()
    customScrollParent.addEventListener('scroll', syncScroll, {
      passive: true,
    })

    return () => {
      customScrollParent.removeEventListener('scroll', syncScroll)
    }
  }, [customScrollParent, ctx, scrollAtom, tableAnchorRef])

  const handleBackToTop = () => {
    const anchor = tableAnchorRef?.current
    if (customScrollParent && anchor) {
      const top =
        customScrollParent.scrollTop +
        anchor.getBoundingClientRect().top -
        customScrollParent.getBoundingClientRect().top -
        (stickyHeaderTop ?? 0)
      customScrollParent.scrollTo({ top, behavior: 'smooth' })
      return
    }

    virtuosoRefInternal.current?.scrollToIndex({
      index: 0,
      behavior: 'smooth',
    })
  }

  const gridTemplateColumns = useMemo(
    () => createColumnGridTemplate(columns),
    [columns],
  )
  const rowStyle = useMemo(
    () => ({ gridTemplateColumns }),
    [gridTemplateColumns],
  )

  const itemAt = (index: number) => itemsState.value?.data[index]

  /**
   * The row's identity, looked up rather than taken from the argument.
   *
   * Virtuoso is driven by `totalCount` and `itemContent(index)`, so it never
   * holds the items and hands `computeItemKey` an `item` that is always
   * undefined — every row was keyed `empty-0`, `empty-1`, by position. The
   * row inside is keyed by the record's own id, so on a feed that prepends,
   * a position kept its wrapper while its contents were torn down and built
   * again: measured on the explore table, 132 rows rebuilt in 42 seconds for
   * eleven visible ones, taking every cell and every icon with them.
   */
  const keyOf = (index: number) => {
    const dataItem = itemAt(index)
    return dataItem ? model.getId(dataItem) : `empty-${index}`
  }

  const renderRowInner = (index: number) => {
    const dataItem = itemAt(index)

    const rowElement = (
      <DataTableRow
        key={keyOf(index)}
        rowData={dataItem}
        renderBackgroundLink={renderBackgroundLink}
        index={index}
        columns={columns}
        rowHeight={rowHeight}
        getRowStyle={getRowStyle}
        model={model}
        context={context}
        onRowClick={onRowClick}
        style={rowStyle}
      />
    )

    if (renderRow && dataItem) {
      return renderRow({
        data: dataItem,
        element: rowElement,
      })
    }

    return rowElement
  }

  const resolvedRowCount = AsyncStateN.isSuccess(itemsState)
    ? // eslint-disable-next-line unicorn/no-nested-ternary
      hasMore
      ? itemsState.value.data.length + 1
      : itemsState.value.data.length
    : 0

  const isEmpty = resolvedRowCount === 0 || AsyncStateN.isLoading(itemsState)

  const components = useMemo(
    (): TableComponents => ({
      Table: Table,
      TableBody: TableBody,
      TableHead: TableHead,
      TableRow: TableRow,
      TableFoot: TableFooter,
      Group: TableGroup,
      FillerRow: FillerRow,

      EmptyPlaceholder: () => (
        <DataTableEmpty
          columns={columns as ReadonlyArray<Column>}
          model={model as unknown as DataTableModel}
          emptyTitle={emptyTitle}
          renderError={renderError}
          renderEmptyMessage={renderEmptyMessage}
        />
      ),
    }),
    [columns, model, emptyTitle, renderError, renderEmptyMessage],
  )

  const FixedHeaderContent = useMemo(() => {
    return () => (
      <DataTableHeader<D, ColumnId, Context>
        columns={columns}
        model={model}
        context={context as Context}
      />
    )
  }, [columns, model, context])

  const style = useMemo(
    () => ({
      overflow: disableScroll ? 'hidden' : 'auto',
    }),
    [disableScroll],
  )

  // The table scrolls away with the external scrollport, so the floating bar has
  // to be pinned to the viewport instead of the table box.
  const backToTopViewportStickyTop =
    customScrollParent && stickyHeaderTop !== undefined
      ? stickyHeaderTop
      : undefined

  return (
    <>
      {withBackToTop || floatingActions ? (
        <DataTableBackToTop
          onBackToTop={handleBackToTop}
          scrollAtom={scrollAtom}
          viewportStickyTop={backToTopViewportStickyTop}
          floatingActions={floatingActions}
        />
      ) : undefined}
      <StyledVirtuoso
        ref={virtuosoRef}
        customScrollParent={customScrollParent}
        fixedHeaderContent={FixedHeaderContent}
        components={components}
        scrollerRef={(ref) => {
          virtuosoScrollerRef.current = ref
        }}
        increaseViewportBy={increaseViewportBy}
        computeItemKey={(index) => keyOf(index)}
        totalCount={resolvedRowCount}
        itemContent={renderRowInner}
        style={style}
        onScroll={handleScroll}
        endReached={maybeLoadMore}
        data-empty={isEmpty}
      />
    </>
  )
}

/** Virtuoso injects these into custom Table* components — they must not reach DOM nodes */
const virtuosoTableShouldForwardProp = (prop: string) =>
  prop !== 'context' && prop !== 'item'

const Table = styled.div
  .withConfig({ shouldForwardProp: virtuosoTableShouldForwardProp })
  .attrs({
    'data-virtuoso-component-table': true,
  } as React.HTMLAttributes<HTMLElement>)``

const TableBody = styled.div
  .withConfig({ shouldForwardProp: virtuosoTableShouldForwardProp })
  .attrs({
    'data-virtuoso-component-table-body': true,
  } as React.HTMLAttributes<HTMLElement>)``

const TableHead = styled.div
  .withConfig({ shouldForwardProp: virtuosoTableShouldForwardProp })
  .attrs({
    'data-virtuoso-component-table-head': true,
  } as React.HTMLAttributes<HTMLElement>)``

const TableRow = styled.div
  .withConfig({ shouldForwardProp: virtuosoTableShouldForwardProp })
  .attrs({
    'data-virtuoso-component-table-row': true,
  } as React.HTMLAttributes<HTMLElement>)``

const TableFooter = styled.div
  .withConfig({ shouldForwardProp: virtuosoTableShouldForwardProp })
  .attrs({
    'data-virtuoso-component-table-footer': true,
  } as React.HTMLAttributes<HTMLElement>)``

const TableGroup = styled.div
  .withConfig({ shouldForwardProp: virtuosoTableShouldForwardProp })
  .attrs({
    'data-virtuoso-component-table-group': true,
  } as React.HTMLAttributes<HTMLElement>)``

const FillerRowBox = styled.div.attrs({
  'data-virtuoso-component-filler-row': true,
} as React.HTMLAttributes<HTMLElement>)`
  display: block;
  box-sizing: border-box;
`
// Must be a div (like TableBody/TableRow), not <tr>: <tr> cannot be a child of
// <div> and causes hydration errors. Height from Virtuoso (FillerRowProps).
const FillerRow: TableComponents['FillerRow'] = ({ height }) => (
  <FillerRowBox style={{ height }} />
)

const StyledVirtuoso = styled(TableVirtuoso)`
  height: 100%;
  /* min-width: 100%; */

  > div {
    width: auto !important;
    min-width: 100%;
  }

  & table {
    width: 100%;

    > thead {
      z-index: 1 !important;
    }
  }

  &[data-empty='true'] {
    & ${Table} {
      height: 100%;
      display: grid;
      grid-template-rows: auto minmax(0, 1fr);
    }
  }
`
